const CHECK_INTERVAL_MS = 5 * 60 * 1000;

export function createAuthSession({ nativeFetch, isLocal, getProviderId, getDevelopmentHeaders, onSession, onError, onExpired }) {
  let pending = null;
  let snapshot = null;
  let checkedAt = 0;
  let providerId = null;
  let hadSession = false;
  let redirecting = false;

  const refresh = (force = false) => {
    if (pending) return pending;
    if (!force && snapshot && Date.now() - checkedAt < CHECK_INTERVAL_MS && providerId === getProviderId()) return Promise.resolve(snapshot);
    const requestedProvider = getProviderId();
    const headers = { 'x-kcs-provider-employee-id': requestedProvider || '167', ...(isLocal ? getDevelopmentHeaders() : {}) };
    pending = Promise.all([
      isLocal ? Promise.resolve(null) : nativeFetch('/.auth/me', { cache: 'no-store' }),
      nativeFetch('/api/me', { cache: 'no-store', redirect: 'manual', headers }),
    ]).then(async ([authResponse, profileResponse]) => {
      if (authResponse?.status >= 500 || profileResponse.status >= 500) throw new Error('Unable to refresh your session. Please retry.');
      const authData = authResponse?.ok ? await authResponse.json() : null;
      const principal = Array.isArray(authData) ? authData[0]?.clientPrincipal : authData?.clientPrincipal;
      const profile = profileResponse.ok && profileResponse.headers?.get('content-type')?.includes('text/html') !== true
        ? await profileResponse.json() : null;
      const expired = !isLocal && (!principal || profileResponse.status === 401 || profileResponse.type === 'opaqueredirect');
      if (!expired && requestedProvider !== getProviderId() && profile?.userRoles?.includes('kcs-super-user')) {
        return { staleProvider: true };
      }
      if (principal) hadSession = true;
      snapshot = { principal: principal || null, profile, expired };
      onSession(snapshot);
      providerId = getProviderId();
      checkedAt = Date.now();
      if (expired && hadSession && !redirecting) {
        redirecting = true;
        onExpired();
      }
      return snapshot;
    }).catch((error) => {
      onError(error);
      throw error;
    }).finally(() => { pending = null; }).then((result) => result.staleProvider ? refresh(true) : result);
    return pending;
  };

  const fetchApi = async (input, options = {}) => {
    const url = new URL(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url, window.location.origin);
    if (url.origin !== window.location.origin || !url.pathname.startsWith('/api/')) return nativeFetch(input, options);
    let current;
    try { current = await refresh(); } catch (error) { current = null; }
    if (current?.expired) return new Response(JSON.stringify({ error: 'Session expired. Please sign in again.' }), { status: 401, headers: { 'Content-Type': 'application/json' } });
    const method = (options.method || input.method || 'GET').toUpperCase();
    const sendRequest = () => {
      const headers = new Headers(input.headers);
      new Headers(options.headers).forEach((value, name) => headers.set(name, value));
      headers.set('x-kcs-provider-employee-id', getProviderId() || '167');
      if (isLocal) {
        for (const [name, value] of Object.entries(getDevelopmentHeaders())) headers.set(name, value);
      }
      return nativeFetch(input, { ...options, headers });
    };
    const response = await sendRequest();
    const loginResponse = response.status === 401 || response.type === 'opaqueredirect'
      || (response.redirected && (response.url.includes('/.auth/') || response.headers?.get('content-type')?.includes('text/html')));
    let authorizationUnavailable = false;
    if (method === 'GET' && response.status === 503) {
      try { authorizationUnavailable = (await response.clone().json()).error === 'Authorization service is unavailable.'; } catch (error) {}
    }
    if (loginResponse || authorizationUnavailable) {
      try { current = await refresh(true); } catch (error) { return response; }
      if (!current.expired && method === 'GET') return sendRequest();
    }
    return response;
  };

  return { refresh, fetchApi };
}